import { getSettingByName } from '@/lib/api/utils/settings-api'
import { useEffect, useState } from 'react'

/**
 * Validade das guias (exame, consulta, procedimento).
 *
 * ESPELHA `apps/backend/src/libs/common/guide-validity/guide-validity.ts` — quem
 * manda no PDF é o backend; aqui é só para as telas de detalhe mostrarem a mesma
 * data que vai sair impressa. Mudou a regra lá, mude aqui.
 *
 * A validade conta a partir da EMISSÃO do documento (`createdAt`), não da data de
 * hoje: reabrir a tela amanhã não renova a validade.
 */

export const GUIDE_VALIDITY_DAYS_SETTING = 'guide_validity_days'

/** Dias de validade quando a empresa não configurou nada. */
export const DEFAULT_GUIDE_VALIDITY_DAYS = 30

const MAX_GUIDE_VALIDITY_DAYS = 3650

/** `0` = sem validade (não exibe nada). Ausente/inválido = default. */
export function normalizeGuideValidityDays(
    raw?: string | number | null
): number {
    if (raw === null || raw === undefined || raw === '') {
        return DEFAULT_GUIDE_VALIDITY_DAYS
    }
    const parsed = typeof raw === 'number' ? raw : parseInt(String(raw), 10)
    if (!Number.isFinite(parsed)) return DEFAULT_GUIDE_VALIDITY_DAYS
    if (parsed <= 0) return 0
    return Math.min(Math.trunc(parsed), MAX_GUIDE_VALIDITY_DAYS)
}

/** Data de validade = emissão + dias. `null` quando desligada. */
export function computeGuideValidUntil(
    issuedAt?: string | Date | null,
    days?: number | null
): Date | null {
    if (!issuedAt || !days || days <= 0) return null
    const base = new Date(issuedAt)
    if (Number.isNaN(base.getTime())) return null
    const validUntil = new Date(base)
    validUntil.setDate(validUntil.getDate() + days)
    return validUntil
}

/**
 * Validade EFETIVA: se o operador renovou a guia (`guideValidUntil` gravado no
 * atendimento), essa data manda e sempre aparece. Sem renovação, cai no cálculo
 * padrão (emissão + dias). ESPELHA `resolveGuideValidUntil` do backend.
 */
export function resolveGuideValidUntil(
    storedGuideValidUntil?: string | Date | null,
    issuedAt?: string | Date | null,
    days?: number | null
): Date | null {
    if (storedGuideValidUntil) {
        const stored = new Date(storedGuideValidUntil)
        if (!Number.isNaN(stored.getTime())) return stored
    }
    return computeGuideValidUntil(issuedAt, days)
}

/**
 * Nova validade ao "Renovar Guia": hoje + dias configurados (default se desligada).
 * Usada só para PRÉVIA na confirmação — quem grava é o backend. ESPELHA o backend.
 */
export function computeRenewedGuideValidUntil(days?: number | null): Date {
    const effectiveDays = days && days > 0 ? days : DEFAULT_GUIDE_VALIDITY_DAYS
    const validUntil = new Date()
    validUntil.setHours(0, 0, 0, 0)
    validUntil.setDate(validUntil.getDate() + effectiveDays)
    return validUntil
}

/**
 * Dias de validade configurados na empresa. Enquanto carrega devolve `null`, para
 * a tela não piscar uma validade errada antes de saber a configuração real.
 */
export function useGuideValidityDays(
    companyId?: number | null
): number | null {
    const [days, setDays] = useState<number | null>(null)

    useEffect(() => {
        if (!companyId) return
        let cancelled = false
        getSettingByName(GUIDE_VALIDITY_DAYS_SETTING, companyId)
            .then(res => {
                if (cancelled) return
                setDays(
                    normalizeGuideValidityDays(
                        res.success && res.result ? res.result.value : null
                    )
                )
            })
            .catch(() => {
                if (!cancelled) setDays(DEFAULT_GUIDE_VALIDITY_DAYS)
            })
        return () => {
            cancelled = true
        }
    }, [companyId])

    return days
}
